import { createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import {
  AccountAssociateInfo,
  AccountAssociateInfoRow,
  AccountDetailsInfo,
  AccountDetailsInfoRow,
  AccountDirectorInfo,
  AccountDirectorInfoRow,
  AssociateDirectorInfo,
  AssociateDirectorInfoRow,
  CollateralSecurityInfo,
  CollateralSecurityInfoRow,
  ConsortiumInfo,
  ConsortiumInfoRow,
  FMR1Fields,
  FMR1Response,
  FraudPerpetratorInfo,
  FraudPerpetratorInfoRow,
  RowMeta,
  StaffAction,
  StaffActionRow,
  ThirdPartServiceProviderInfo,
  ThirdPartServiceProviderInfoRow,
  UpdateFMR1Request,
} from "../../../api/fmr1";
import type { RootState } from "../../../app/store";
import { fetchFMR1Thunk, upsertFMR1Thunk } from "./frm1Thunk";

export type FMR1AsyncStatus = "idle" | "pending" | "succeeded" | "failed";

export type DraftFMR1Fields = FMR1Fields & RowMeta;

export interface FMR1State {
  caseId: string | null;  
  saved_data: {
    fmr_fields: FMR1Fields;
    consortium_rows: EntityState<ConsortiumInfo, string>;
    staff_side_action_rows: EntityState<StaffAction, string>;
    account_details_rows: EntityState<AccountDetailsInfo, string>;
    perpetrator_info_rows: EntityState<FraudPerpetratorInfo, string>;
    account_director_info_rows: EntityState<AccountDirectorInfo, string>;
    associate_concern_rows: EntityState<AccountAssociateInfo, string>;
    associate_director_info_rows: EntityState<AssociateDirectorInfo, string>;
    collateral_info_rows: EntityState<CollateralSecurityInfo, string>;
    tpsp_info_rows: EntityState<ThirdPartServiceProviderInfo, string>;
  };
  draft_data: {
    fmr_fields: DraftFMR1Fields;
    consortium_rows: EntityState<ConsortiumInfoRow, string>;
    staff_side_action_rows: EntityState<StaffActionRow, string>;
    account_details_rows: EntityState<AccountDetailsInfoRow, string>;
    perpetrator_info_rows: EntityState<FraudPerpetratorInfoRow, string>;
    account_director_info_rows: EntityState<AccountDirectorInfoRow, string>;
    associate_concern_rows: EntityState<AccountAssociateInfoRow, string>;
    associate_director_info_rows: EntityState<AssociateDirectorInfoRow, string>;
    collateral_info_rows: EntityState<CollateralSecurityInfoRow, string>;
    tpsp_info_rows: EntityState<ThirdPartServiceProviderInfoRow, string>;
  };
  fetch_status: FMR1AsyncStatus;
  fetch_error?: string;
  upsert_status: FMR1AsyncStatus;
  upsert_error?: string;
}

const consortiumInfoAdapter = createEntityAdapter<ConsortiumInfo, string>({
  selectId: (row) => row.row_id,
});

const uiConsortiumAdapter = createEntityAdapter<ConsortiumInfoRow, string>({
  selectId: (row) => row.row_id,
});

const staffSideActionAdapter = createEntityAdapter<StaffAction, string>({
  selectId: (row) => row.row_id,
});

const uiStaffAdapter = createEntityAdapter<StaffActionRow, string>({
  selectId: (row) => row.row_id,
});

const accountDetailsAdapter = createEntityAdapter<AccountDetailsInfo, string>({
  selectId: (row) => row.row_id,
});

const uiAccountDetailsAdapter = createEntityAdapter<AccountDetailsInfoRow, string>({
  selectId: (row) => row.row_id,
});

const perpetratorDetailsAdapter = createEntityAdapter<FraudPerpetratorInfo, string>({
  selectId: (row) => row.row_id,
});

const uiPerpetratorAdapter = createEntityAdapter<FraudPerpetratorInfoRow, string>({
  selectId: (row) => row.row_id,
});

const directorPartnerAdapter = createEntityAdapter<AccountDirectorInfo, string>({
  selectId: (row) => row.row_id,
});

const uiDirectorPartnerAdapter = createEntityAdapter<AccountDirectorInfoRow, string>({
  selectId: (row) => row.row_id,
});

const associateConcernAdapter = createEntityAdapter<AccountAssociateInfo, string>({
  selectId: (row) => row.row_id,
});

const uiAssociateConcernAdapter = createEntityAdapter<AccountAssociateInfoRow, string>({
  selectId: (row) => row.row_id,
});

const associateDirectorAdapter = createEntityAdapter<AssociateDirectorInfo, string>({
  selectId: (row) => row.row_id,
});

const uiAssociateDirectorAdapter = createEntityAdapter<AssociateDirectorInfoRow, string>({
  selectId: (row) => row.row_id,
});

const collateralAdapter = createEntityAdapter<CollateralSecurityInfo, string>({
  selectId: (row) => row.row_id,
});

const uiCollateralAdapter = createEntityAdapter<CollateralSecurityInfoRow, string>({
  selectId: (row) => row.row_id,
});

const tpspAdapter = createEntityAdapter<ThirdPartServiceProviderInfo, string>({
  selectId: (row) => row.row_id,
});

const uiTpspAdapter = createEntityAdapter<ThirdPartServiceProviderInfoRow, string>({
  selectId: (row) => row.row_id,
});

const initialState: FMR1State = {
  caseId: null,
  saved_data: {
    fmr_fields: {},
    consortium_rows: consortiumInfoAdapter.getInitialState(),
    staff_side_action_rows: staffSideActionAdapter.getInitialState(),
    account_details_rows: accountDetailsAdapter.getInitialState(),
    perpetrator_info_rows: perpetratorDetailsAdapter.getInitialState(),
    account_director_info_rows: directorPartnerAdapter.getInitialState(),
    associate_concern_rows: associateConcernAdapter.getInitialState(),
    associate_director_info_rows: associateDirectorAdapter.getInitialState(),
    collateral_info_rows: collateralAdapter.getInitialState(),
    tpsp_info_rows: tpspAdapter.getInitialState(),
  },
  draft_data: {
    fmr_fields: {},
    consortium_rows: uiConsortiumAdapter.getInitialState(),
    staff_side_action_rows: uiStaffAdapter.getInitialState(),
    account_details_rows: uiAccountDetailsAdapter.getInitialState(),
    perpetrator_info_rows: uiPerpetratorAdapter.getInitialState(),
    account_director_info_rows: uiDirectorPartnerAdapter.getInitialState(),
    associate_concern_rows: uiAssociateConcernAdapter.getInitialState(),
    associate_director_info_rows: uiAssociateDirectorAdapter.getInitialState(),
    collateral_info_rows: uiCollateralAdapter.getInitialState(),
    tpsp_info_rows: uiTpspAdapter.getInitialState(),
  },
  fetch_status: "idle",
  fetch_error: undefined,
  upsert_status: "idle",
  upsert_error: undefined,
};

const stripDraftMeta = (row: any) => {
  const { isDirty: _d, isAdded: _a, isDeleted: _x, ...rest } = row;
  return rest;
};

const isRowBlank = (row: any): boolean => {
  const ignore = new Set(["id", "row_id", "isDirty", "isAdded", "isDeleted", "sno", "sort_order"]);
  for (const [k, v] of Object.entries(row ?? {})) {
    if (ignore.has(k)) continue;
    if (v !== null && v !== undefined && v !== "") return false;
  }
  return true;
};

const stripForCompare = (obj: any): Record<string, unknown> => {
  const { isDirty: _d, isAdded: _a, isDeleted: _x, id: _id, ...rest } = obj ?? {};
  return rest;
};

const shallowStrictEqual = (a: any, b: any): boolean => {
  const aa = a ?? {};
  const bb = b ?? {};
  for (const k of Object.keys(aa)) if (aa[k] !== bb[k]) return false;
  for (const k of Object.keys(bb)) if (!(k in aa)) return false;
  return true;
};

const isFMR1TableDirty = (savedRows: any[], draftRows: any[]): boolean => {
  const savedById = new Map<string, any>();
  for (const s of savedRows) savedById.set(s.row_id, s);

  const seenIds = new Set<string>();
  for (const draft of draftRows) {
    if (!draft) continue;
    seenIds.add(draft.row_id);

    if (draft.isAdded) {
      if (!draft.isDeleted && !isRowBlank(draft)) return true;
      continue;
    }
    if (!savedById.has(draft.row_id)) return true;
    if (draft.isDeleted) return true;
    if (!shallowStrictEqual(stripForCompare(draft), stripForCompare(savedById.get(draft.row_id)!))) return true;
  }

  for (const s of savedRows) if (!seenIds.has(s.row_id)) return true;
  return false;
};

const consortiumSelectors = consortiumInfoAdapter.getSelectors()
const staffSideActionSelectors = staffSideActionAdapter.getSelectors()
const accountDetailsSelectors = accountDetailsAdapter.getSelectors()
const perpetratorDetailsSelectors = perpetratorDetailsAdapter.getSelectors()
const accountDirectorSelectors = directorPartnerAdapter.getSelectors()
const associateConcernSelectors = associateConcernAdapter.getSelectors()
const associateDirectorSelectors = associateDirectorAdapter.getSelectors()
const collateralSelectors = collateralAdapter.getSelectors()
const tspsSelectors = tpspAdapter.getSelectors()

const uiConsortiumSelectors = uiConsortiumAdapter.getSelectors()
const uiStaffSideActionSelectors = uiStaffAdapter.getSelectors()
const uiAccountDetailsSelectors = uiAccountDetailsAdapter.getSelectors()
const uiPerpetratorDetailsSelectors = uiPerpetratorAdapter.getSelectors()
const uiAccountDirectorSelectors = uiDirectorPartnerAdapter.getSelectors()
const uiAssociateConcernSelectors = uiAssociateConcernAdapter.getSelectors()
const uiAssociateDirectorSelectors = uiAssociateDirectorAdapter.getSelectors()
const uiCollateralSelectors = uiCollateralAdapter.getSelectors()
const uiTspsSelectors = uiTpspAdapter.getSelectors()

const resetDraftFromSaved = (state: FMR1State) => {
  state.draft_data.fmr_fields = { ...(state.saved_data.fmr_fields ?? {}) };

  const consortium_rows = consortiumSelectors.selectAll(state.saved_data.consortium_rows);
  uiConsortiumAdapter.setAll(state.draft_data.consortium_rows, consortium_rows);

  const staff_side_action_rows = staffSideActionSelectors.selectAll(state.saved_data.staff_side_action_rows);
  uiStaffAdapter.setAll(state.draft_data.staff_side_action_rows, staff_side_action_rows);

  const account_details_rows = accountDetailsSelectors.selectAll(state.saved_data.account_details_rows);
  uiAccountDetailsAdapter.setAll(state.draft_data.account_details_rows, account_details_rows);

  const perpetrator_info_rows = perpetratorDetailsSelectors.selectAll(state.saved_data.perpetrator_info_rows);
  uiPerpetratorAdapter.setAll(state.draft_data.perpetrator_info_rows, perpetrator_info_rows);

  const account_director_info_rows = accountDirectorSelectors.selectAll(state.saved_data.account_director_info_rows);
  uiDirectorPartnerAdapter.setAll(state.draft_data.account_director_info_rows, account_director_info_rows);

  const associate_concern_rows = associateConcernSelectors.selectAll(state.saved_data.associate_concern_rows);
  uiAssociateConcernAdapter.setAll(state.draft_data.associate_concern_rows, associate_concern_rows);

  const associate_director_info_rows = associateDirectorSelectors.selectAll(state.saved_data.associate_director_info_rows);
  uiAssociateDirectorAdapter.setAll(state.draft_data.associate_director_info_rows, associate_director_info_rows);

  const collateral_info_rows = collateralSelectors.selectAll(state.saved_data.collateral_info_rows);
  uiCollateralAdapter.setAll(state.draft_data.collateral_info_rows, collateral_info_rows);

  const tpsp_info_rows = tspsSelectors.selectAll(state.saved_data.tpsp_info_rows);
  uiTpspAdapter.setAll(state.draft_data.tpsp_info_rows, tpsp_info_rows);
};

const applyFMR1Payload = (state: FMR1State, payload: FMR1Response) => {
  state.saved_data.fmr_fields = payload?.fmr1_fields ?? {};
  consortiumInfoAdapter.setAll(state.saved_data.consortium_rows, (payload.consortium_info ?? []));
  staffSideActionAdapter.setAll(state.saved_data.staff_side_action_rows, (payload.staff_side_action ?? []));
  accountDetailsAdapter.setAll(state.saved_data.account_details_rows,(payload.account_details ?? []));
  perpetratorDetailsAdapter.setAll(state.saved_data.perpetrator_info_rows, (payload.perpetrator_info ?? []));
  directorPartnerAdapter.setAll(state.saved_data.account_director_info_rows, (payload.account_director_info ?? []));
  associateConcernAdapter.setAll(state.saved_data.associate_concern_rows, (payload.associate_concern ?? []));
  associateDirectorAdapter.setAll(state.saved_data.associate_director_info_rows, (payload.associate_director_info ?? []));
  collateralAdapter.setAll(state.saved_data.collateral_info_rows, (payload.collateral_info ?? []));
  tpspAdapter.setAll(state.saved_data.tpsp_info_rows, (payload.tpsp_info ?? []));
  resetDraftFromSaved(state);
};

const applyFMR1UpdateRequest = (state: FMR1State, _request: UpdateFMR1Request) => {
  const { isDirty: _d, ...cleanFmrFields } = state.draft_data.fmr_fields ?? {};
  state.saved_data.fmr_fields = cleanFmrFields;
  state.draft_data.fmr_fields = { ...cleanFmrFields, isDirty: false };

  const consortiumDraft = uiConsortiumSelectors.selectAll(state.draft_data.consortium_rows).filter((r) => !r.isDeleted);
  consortiumInfoAdapter.setAll(state.saved_data.consortium_rows, consortiumDraft.map(stripDraftMeta));
  uiConsortiumAdapter.setAll(state.draft_data.consortium_rows, consortiumDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const staffDraft = uiStaffSideActionSelectors.selectAll(state.draft_data.staff_side_action_rows).filter((r) => !r.isDeleted);
  staffSideActionAdapter.setAll(state.saved_data.staff_side_action_rows, staffDraft.map(stripDraftMeta));
  uiStaffAdapter.setAll(state.draft_data.staff_side_action_rows, staffDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const accountDraft = uiAccountDetailsSelectors.selectAll(state.draft_data.account_details_rows).filter((r) => !r.isDeleted);
  accountDetailsAdapter.setAll(state.saved_data.account_details_rows, accountDraft.map(stripDraftMeta));
  uiAccountDetailsAdapter.setAll(state.draft_data.account_details_rows, accountDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const perpetratorDraft = uiPerpetratorDetailsSelectors.selectAll(state.draft_data.perpetrator_info_rows).filter((r) => !r.isDeleted);
  perpetratorDetailsAdapter.setAll(state.saved_data.perpetrator_info_rows, perpetratorDraft.map(stripDraftMeta));
  uiPerpetratorAdapter.setAll(state.draft_data.perpetrator_info_rows, perpetratorDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const directorDraft = uiAccountDirectorSelectors.selectAll(state.draft_data.account_director_info_rows).filter((r) => !r.isDeleted);
  directorPartnerAdapter.setAll(state.saved_data.account_director_info_rows, directorDraft.map(stripDraftMeta));
  uiDirectorPartnerAdapter.setAll(state.draft_data.account_director_info_rows, directorDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const associateDraft = uiAssociateConcernSelectors.selectAll(state.draft_data.associate_concern_rows).filter((r) => !r.isDeleted);
  associateConcernAdapter.setAll(state.saved_data.associate_concern_rows, associateDraft.map(stripDraftMeta));
  uiAssociateConcernAdapter.setAll(state.draft_data.associate_concern_rows, associateDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const associateDirDraft = uiAssociateDirectorSelectors.selectAll(state.draft_data.associate_director_info_rows).filter((r) => !r.isDeleted && !(r.isAdded && isRowBlank(r)));
  associateDirectorAdapter.setAll(state.saved_data.associate_director_info_rows, associateDirDraft.map(stripDraftMeta));
  uiAssociateDirectorAdapter.setAll(state.draft_data.associate_director_info_rows, associateDirDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const collateralDraft = uiCollateralSelectors.selectAll(state.draft_data.collateral_info_rows).filter((r) => !r.isDeleted);
  collateralAdapter.setAll(state.saved_data.collateral_info_rows, collateralDraft.map(stripDraftMeta));
  uiCollateralAdapter.setAll(state.draft_data.collateral_info_rows, collateralDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));

  const tpspDraft = uiTspsSelectors.selectAll(state.draft_data.tpsp_info_rows).filter((r) => !r.isDeleted);
  tpspAdapter.setAll(state.saved_data.tpsp_info_rows, tpspDraft.map(stripDraftMeta));
  uiTpspAdapter.setAll(state.draft_data.tpsp_info_rows, tpspDraft.map((r) => ({...r, isDirty: false, isAdded: false, isDeleted: false })));
};

const frm1Slice = createSlice({
  name: "fmr1",
  initialState,
  reducers: {
    clearFMR1State: () => initialState,
    updateFMR1DraftField: (state, action: { payload: { key: keyof DraftFMR1Fields; value: any } }) => {
      const { key, value } = action.payload;
      state.draft_data.fmr_fields = { ...(state.draft_data.fmr_fields ?? {}), [key]: value, isDirty: true };
    },
    addConsortiumRow: (state, action: { payload: { row: ConsortiumInfoRow } }) => {
      uiConsortiumAdapter.addOne(state.draft_data.consortium_rows, action.payload.row);
    },
    updateConsortiumRow: (state, action: { payload: { id: string; changes: Partial<ConsortiumInfoRow> } }) => {
      uiConsortiumAdapter.updateOne(state.draft_data.consortium_rows, {id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteConsortiumRow: (state, action: { payload: { id: string } }) => {
      const row = uiConsortiumSelectors.selectById(state.draft_data.consortium_rows, action.payload.id);
      if (row.isAdded) {
        uiConsortiumAdapter.removeOne(state.draft_data.consortium_rows, action.payload.id);
      } else {
        uiConsortiumAdapter.updateOne(state.draft_data.consortium_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addStaffSideActionRow: (state, action: { payload: { row: StaffActionRow } }) => {
      uiStaffAdapter.addOne(state.draft_data.staff_side_action_rows, action.payload.row);
    },
    updateStaffSideActionRow: (state, action: { payload: { id: string; changes: Partial<StaffActionRow> } }) => {
      uiStaffAdapter.updateOne(state.draft_data.staff_side_action_rows, { id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteStaffSideActionRow: (state, action: { payload: { id: string } }) => {
      const row = uiStaffSideActionSelectors.selectById(state.draft_data.staff_side_action_rows, action.payload.id);
      if (row.isAdded) {
        uiStaffAdapter.removeOne(state.draft_data.staff_side_action_rows, action.payload.id);
      } else {
        uiStaffAdapter.updateOne(state.draft_data.staff_side_action_rows, { id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addAccountDetailsRow: (state, action: { payload: { row: AccountDetailsInfoRow } }) => {
      uiAccountDetailsAdapter.addOne(state.draft_data.account_details_rows, action.payload.row);
    },
    updateAccountDetailsRow: (state, action: { payload: { id: string; changes: Partial<AccountDetailsInfoRow> } }) => {
      uiAccountDetailsAdapter.updateOne(state.draft_data.account_details_rows, {id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteAccountDetailsRow: (state, action: { payload: { id: string } }) => {
      const row = uiAccountDetailsSelectors.selectById(state.draft_data.account_details_rows, action.payload.id);
      if (row.isAdded) {
        uiAccountDetailsAdapter.removeOne(state.draft_data.account_details_rows, action.payload.id);
      } else {
        uiAccountDetailsAdapter.updateOne(state.draft_data.account_details_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addPerpetratorInfoRow: (state, action: { payload: { row: FraudPerpetratorInfoRow } }) => {
      uiPerpetratorAdapter.addOne(state.draft_data.perpetrator_info_rows, action.payload.row);
    },
    updatePerpetratorInfoRow: (state, action: { payload: { id: string; changes: Partial<FraudPerpetratorInfoRow> } }) => {
      uiPerpetratorAdapter.updateOne(state.draft_data.perpetrator_info_rows, {id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deletePerpetratorInfoRow: (state, action: { payload: { id: string } }) => {
      const row = uiPerpetratorDetailsSelectors.selectById(state.draft_data.perpetrator_info_rows, action.payload.id);
      if (row.isAdded) {
        uiPerpetratorAdapter.removeOne(state.draft_data.perpetrator_info_rows, action.payload.id);
      } else {
        uiPerpetratorAdapter.updateOne(state.draft_data.perpetrator_info_rows, { id: action.payload.id, changes: { isDeleted: true, isDirty: true } as any});
      }
      
    },
    addDirectorPartnerRow: (state, action: { payload: { row: AccountDirectorInfoRow } }) => {
      uiDirectorPartnerAdapter.addOne(state.draft_data.account_director_info_rows, action.payload.row);
    },
    updateDirectorPartnerRow: (state, action: { payload: { id: string; changes: Partial<AccountDirectorInfoRow> } }) => {
      uiDirectorPartnerAdapter.updateOne(state.draft_data.account_director_info_rows, {id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteDirectorPartnerRow: (state, action: { payload: { id: string } }) => {
      const row = uiAccountDirectorSelectors.selectById(state.draft_data.account_director_info_rows, action.payload.id);
      if (row.isAdded) {
        uiDirectorPartnerAdapter.removeOne(state.draft_data.account_director_info_rows, action.payload.id);
      } else {
        uiDirectorPartnerAdapter.updateOne(state.draft_data.account_director_info_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addAssociateConcernRow: (state, action: { payload: { row: AccountAssociateInfo } }) => {
      uiAssociateConcernAdapter.addOne(state.draft_data.associate_concern_rows, action.payload.row);
    },
    updateAssociateConcernRow: (state, action: { payload: { id: string; changes: Partial<AccountAssociateInfo> } }) => {
      uiAssociateConcernAdapter.updateOne(state.draft_data.associate_concern_rows, {
        id: action.payload.id,
        changes: { ...(action.payload.changes as any), isDirty: true } as any,
      });
    },
    deleteAssociateConcernRow: (state, action: { payload: { id: string } }) => {
      const row = uiAssociateConcernSelectors.selectById(state.draft_data.associate_concern_rows, action.payload.id);
      if (row.isAdded) {
        uiAssociateConcernAdapter.removeOne(state.draft_data.associate_concern_rows, action.payload.id);
      } else {
        uiAssociateConcernAdapter.updateOne(state.draft_data.associate_concern_rows, {
          id: action.payload.id,
          changes: { isDeleted: true, isDirty: true } as any,
        });
      }
    },
    addAssociateDirectorInfoRow: (state, action: { payload: { row: AssociateDirectorInfoRow } }) => {
      uiAssociateDirectorAdapter.addOne(state.draft_data.associate_director_info_rows, action.payload.row);
    },
    updateAssociateDirectorInfoRow: (
      state,
      action: { payload: { id: string; changes: Partial<AssociateDirectorInfoRow> } },
    ) => {
      uiAssociateDirectorAdapter.updateOne(state.draft_data.associate_director_info_rows, { id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteAssociateDirectorInfoRow: (state, action: { payload: { id: string } }) => {
      const row = uiAssociateDirectorSelectors.selectById(state.draft_data.associate_director_info_rows, action.payload.id);
      if (row.isAdded) {
        uiAssociateDirectorAdapter.removeOne(state.draft_data.associate_director_info_rows, action.payload.id);
      } else {
        uiAssociateDirectorAdapter.updateOne(state.draft_data.associate_director_info_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addCollateralInfoRow: (state, action: { payload: { row: CollateralSecurityInfoRow } }) => {
      uiCollateralAdapter.addOne(state.draft_data.collateral_info_rows, action.payload.row);
    },
    updateCollateralInfoRow: (state, action: { payload: { id: string; changes: Partial<CollateralSecurityInfoRow> } }) => {
      uiCollateralAdapter.updateOne(state.draft_data.collateral_info_rows, { id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteCollateralInfoRow: (state, action: { payload: { id: string } }) => {
      const row = uiCollateralSelectors.selectById(state.draft_data.collateral_info_rows, action.payload.id);
      if (row.isAdded) {
        uiCollateralAdapter.removeOne(state.draft_data.collateral_info_rows, action.payload.id);
      } else {
        uiCollateralAdapter.updateOne(state.draft_data.collateral_info_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    addTpspInfoRow: (state, action: { payload: { row: ThirdPartServiceProviderInfoRow } }) => {
      uiTpspAdapter.addOne(state.draft_data.tpsp_info_rows, action.payload.row);
    },
    updateTpspInfoRow: (state, action: { payload: { id: string; changes: Partial<ThirdPartServiceProviderInfoRow> } }) => {
      uiTpspAdapter.updateOne(state.draft_data.tpsp_info_rows, {id: action.payload.id, changes: { ...action.payload.changes, isDirty: true }});
    },
    deleteTpspInfoRow: (state, action: { payload: { id: string } }) => {
      const row = uiTspsSelectors.selectById(state.draft_data.tpsp_info_rows, action.payload.id);
      if (row.isAdded) {
        uiTpspAdapter.removeOne(state.draft_data.tpsp_info_rows, action.payload.id);
      } else {
        uiTpspAdapter.updateOne(state.draft_data.tpsp_info_rows, {id: action.payload.id, changes: { isDeleted: true, isDirty: true }});
      }
    },
    discardFMR1UiChanges: (state) => {
      resetDraftFromSaved(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFMR1Thunk.pending, (state) => {
        return {
            ...initialState,
            fetch_status: "pending",
        };
      })
      .addCase(fetchFMR1Thunk.fulfilled, (state, action) => {
        state.caseId = action.meta.arg;
        applyFMR1Payload(state, action.payload);
        state.fetch_status = "succeeded";
        state.fetch_error = undefined;
      })
      .addCase(fetchFMR1Thunk.rejected, (state, action) => {
        if ((action.meta as any)?.condition || (action.meta as any)?.aborted) return;
        state.fetch_status = "failed";
        state.fetch_error = (action.payload as any) || action.error?.message || "Failed to fetch FMR1";
      })
      .addCase(upsertFMR1Thunk.pending, (state) => {
        state.upsert_status = "pending";
        state.upsert_error = undefined;
      })
      .addCase(upsertFMR1Thunk.fulfilled, (state, action) => {
        applyFMR1UpdateRequest(state, action.meta.arg.request);
        state.upsert_status = "succeeded";
        state.upsert_error = undefined;
      })
      .addCase(upsertFMR1Thunk.rejected, (state, action) => {
        if ((action.meta as any)?.aborted) return;
        state.upsert_status = "failed";
        state.upsert_error = (action.payload as any) || action.error?.message || "Failed to upsert FMR1";
      });
  },
});

export const {
  clearFMR1State,
  updateFMR1DraftField,
  addConsortiumRow,
  updateConsortiumRow,
  deleteConsortiumRow,
  addStaffSideActionRow,
  updateStaffSideActionRow,
  deleteStaffSideActionRow,
  addAccountDetailsRow,
  updateAccountDetailsRow,
  deleteAccountDetailsRow,
  addPerpetratorInfoRow,
  updatePerpetratorInfoRow,
  deletePerpetratorInfoRow,
  addDirectorPartnerRow,
  updateDirectorPartnerRow,
  deleteDirectorPartnerRow,
  addAssociateConcernRow,
  updateAssociateConcernRow,
  deleteAssociateConcernRow,
  addAssociateDirectorInfoRow,
  updateAssociateDirectorInfoRow,
  deleteAssociateDirectorInfoRow,
  addCollateralInfoRow,
  updateCollateralInfoRow,
  deleteCollateralInfoRow,
  addTpspInfoRow,
  updateTpspInfoRow,
  deleteTpspInfoRow,
  discardFMR1UiChanges,
} = frm1Slice.actions;

export const fmr1Reducer = frm1Slice.reducer;

const selectFMR1State = (state: RootState): FMR1State => (state as any).fmr1Data;

export const selectFMR1DraftFmrFields = (state: RootState): DraftFMR1Fields =>
  selectFMR1State(state).draft_data.fmr_fields;

export const selectFMR1DraftConsortiumRows = (state: RootState): ConsortiumInfoRow[] =>
  uiConsortiumSelectors.selectAll(selectFMR1State(state).draft_data.consortium_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftStaffSideActionRows = (state: RootState): StaffActionRow[] =>
  uiStaffSideActionSelectors.selectAll(selectFMR1State(state).draft_data.staff_side_action_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftAccountDetailsRows = (state: RootState): AccountDetailsInfoRow[] =>
  uiAccountDetailsSelectors.selectAll(selectFMR1State(state).draft_data.account_details_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftPerpetratorInfoRows = (state: RootState): FraudPerpetratorInfoRow[] =>
  uiPerpetratorDetailsSelectors.selectAll(selectFMR1State(state).draft_data.perpetrator_info_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftAccountDirectorInfoRows = (state: RootState): AccountDirectorInfoRow[] =>
  uiAccountDirectorSelectors.selectAll(selectFMR1State(state).draft_data.account_director_info_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftAssociateConcernRows = (state: RootState): AccountAssociateInfoRow[] =>
  uiAssociateConcernSelectors.selectAll(selectFMR1State(state).draft_data.associate_concern_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftAssociateDirectorInfoRows = (state: RootState): AssociateDirectorInfoRow[] =>
  uiAssociateDirectorSelectors.selectAll(selectFMR1State(state).draft_data.associate_director_info_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftCollateralInfoRows = (state: RootState): CollateralSecurityInfoRow[] =>
  uiCollateralSelectors.selectAll(selectFMR1State(state).draft_data.collateral_info_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1DraftTpspInfoRows = (state: RootState): ThirdPartServiceProviderInfoRow[] =>
  uiTspsSelectors.selectAll(selectFMR1State(state).draft_data.tpsp_info_rows).filter((r: any) => !r.isDeleted);

export const selectFMR1UpsertRequestFromDraft = (state: RootState): UpdateFMR1Request => {
  const fmr_state = selectFMR1State(state);

  const fmr_fields = fmr_state.draft_data.fmr_fields;

  const { isDirty: _isDirty, ...cleanFmrFields } = fmr_fields ?? {};
  const isFmrFieldsEmpty = Object.values(cleanFmrFields).every((val) => val === undefined);
  const upsertFMR1Fields = isFmrFieldsEmpty ? { general_information_name: "" } : (fmr_fields?.isDirty ? cleanFmrFields : undefined);

  const consortiumDraftAll = uiConsortiumSelectors.selectAll(fmr_state.draft_data.consortium_rows);
  const consortiumDirtyRows = consortiumDraftAll
    .filter((r) => !r.isDeleted && (r.isDirty || r.isAdded))
    .map(stripDraftMeta);
  const consortiumUpsert = consortiumDirtyRows.length
    ? consortiumDirtyRows.map((item: any, idx: number) => ({
        ...item,
        sortOrder: item.sortOrder ?? item.sort_order ?? idx + 1,
      }))
    : undefined;
  const consortiumDeleteIds = consortiumDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const staffDraftAll = uiStaffSideActionSelectors.selectAll(fmr_state.draft_data.staff_side_action_rows);
  const staffDirtyRows = staffDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const staffActionUpsert = staffDirtyRows.length? staffDirtyRows : undefined;
  const staffActionDeleteIds = staffDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const accountDetailsDraftAll = uiAccountDetailsSelectors.selectAll(fmr_state.draft_data.account_details_rows);
  const accountDetailsRows = accountDetailsDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const accountDetailsUpsert = accountDetailsRows.length? accountDetailsRows : undefined;
  const accountDetailsDeleteIds = accountDetailsDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const perpetratorDetailsDraftAll = uiPerpetratorDetailsSelectors.selectAll(fmr_state.draft_data.perpetrator_info_rows);
  const perpetratorDetailsDirtyRows = perpetratorDetailsDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const perpetratorDetailsUpsert = perpetratorDetailsDirtyRows.length? perpetratorDetailsDirtyRows : undefined;
  const perpetratorDetailsDeleteIds = perpetratorDetailsDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const accountDirectorDraftAll = uiAccountDirectorSelectors.selectAll(fmr_state.draft_data.account_director_info_rows);
  const accountDirectorDirtyRows = accountDirectorDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const accountDirectorUpsert = accountDirectorDirtyRows.length? accountDirectorDirtyRows : undefined;
  const accountDirectorDeleteIds = accountDirectorDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const associateConcernsDraftAll = uiAssociateConcernSelectors.selectAll(fmr_state.draft_data.associate_concern_rows);
  const associateConcernsDirtyRows = associateConcernsDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const associateConcernsUpsert = associateConcernsDirtyRows.length? associateConcernsDirtyRows : undefined;
  const associateConcernsDeleteIds = associateConcernsDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const associateDirectorDraftAll = uiAssociateDirectorSelectors.selectAll(fmr_state.draft_data.associate_director_info_rows);
  const associateDirectorsDirtyRows = associateDirectorDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const associateDirctorUpsert = associateDirectorsDirtyRows.length? associateDirectorsDirtyRows : undefined;
  const associateDirectorDeleteIds = associateDirectorDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const collateralDraftAll = uiCollateralSelectors.selectAll(fmr_state.draft_data.collateral_info_rows);
  const collateralDirtyRows = collateralDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const collateralUpsert = collateralDirtyRows.length? collateralDirtyRows : undefined;
  const collateralDeleteIds = collateralDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const tspsDraftAll = uiTspsSelectors.selectAll(fmr_state.draft_data.tpsp_info_rows);
  const tspsDirtyRows = tspsDraftAll.filter((r) => !r.isDeleted && (r.isDirty || r.isAdded)).map(stripDraftMeta);
  const tspsUpsert = tspsDirtyRows.length? tspsDirtyRows : undefined;
  const tspsDeleteIds = tspsDraftAll.filter((r) => r.isDeleted && !r.isAdded).map((r) => r.row_id);

  const req: UpdateFMR1Request = {
    upsert: {
      fmr_fields: upsertFMR1Fields,
      consortium_info: consortiumUpsert,
      staff_side_action: staffActionUpsert,
      account_details: accountDetailsUpsert,
      perpetrator_info: perpetratorDetailsUpsert,
      account_director_info: accountDirectorUpsert,
      associate_concern: associateConcernsUpsert,
      associate_director_info: associateDirctorUpsert,
      collateral_info: collateralUpsert,
      tpsp_info: tspsUpsert
    },
    delete: {
      consortium_info_ids: consortiumDeleteIds,
      staff_side_action_ids: staffActionDeleteIds,
      account_details_ids: accountDetailsDeleteIds,
      perpetrator_info_ids: perpetratorDetailsDeleteIds,
      account_director_info_ids: accountDirectorDeleteIds,
      associate_concern_ids: associateConcernsDeleteIds,
      associate_director_info_ids: associateDirectorDeleteIds,
      collateral_info_ids: collateralDeleteIds,
      tpsp_info_ids: tspsDeleteIds
    }
  };
  return req;
};

const DIRTY_CHECK_TABLES: Array<{
  key: keyof FMR1State["saved_data"] & keyof FMR1State["draft_data"];
  savedSel: { selectAll: (s: any) => any[] };
  draftSel: { selectAll: (s: any) => any[] };
}> = [
  { key: "consortium_rows", savedSel: consortiumSelectors, draftSel: uiConsortiumSelectors },
  { key: "staff_side_action_rows", savedSel: staffSideActionSelectors, draftSel: uiStaffSideActionSelectors },
  { key: "account_details_rows", savedSel: accountDetailsSelectors, draftSel: uiAccountDetailsSelectors },
  { key: "perpetrator_info_rows", savedSel: perpetratorDetailsSelectors, draftSel: uiPerpetratorDetailsSelectors },
  { key: "account_director_info_rows", savedSel: accountDirectorSelectors, draftSel: uiAccountDirectorSelectors },
  { key: "associate_concern_rows", savedSel: associateConcernSelectors, draftSel: uiAssociateConcernSelectors },
  { key: "associate_director_info_rows", savedSel: associateDirectorSelectors, draftSel: uiAssociateDirectorSelectors },
  { key: "collateral_info_rows", savedSel: collateralSelectors, draftSel: uiCollateralSelectors },
  { key: "tpsp_info_rows", savedSel: tspsSelectors, draftSel: uiTspsSelectors },
];

export const selectFMR1IsDirtyOverall = (state: RootState): boolean => {
  const s = selectFMR1State(state);
  if (!shallowStrictEqual(stripForCompare(s.draft_data.fmr_fields), s.saved_data.fmr_fields)) return true;
  for (const t of DIRTY_CHECK_TABLES) {
    if (isFMR1TableDirty(t.savedSel.selectAll(s.saved_data[t.key]), t.draftSel.selectAll(s.draft_data[t.key]))) return true;
  }
  return false;
};
